<script context="module">
    import { get } from "svelte/store";
    import { apiMethods } from "../../../Services/index.svelte";
    import { accountDoctors, EncounterSidePanelProps } from "../../../stores";
    import { getDAN, getPID } from "../../../Utils/DataSyncFunctions.svelte";
    import {
        FallBackEncounterDurationMins,
        insuranceTypesMayAllowAmaBillingRule,
        insuranceTypesNotAllowBillingRule,
    } from "../../../Utils/utilConstants.svelte";

    import {
        addHoursToDate,
        getAmountInCurrency,
        getBillingType,
        getDate,
        getDateFromDateString,
        getEncounterPaymentDropdownOption,
        getFormatDateAndTime,
        getHHMM,
        getTitle,
        getOOPType,
        getPatientAge,
        getTimeDiffInMillSec,
        getValidBillingData,
        isObjectEmpty,
        showError,
        getPaymentTag,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        DirectInsuranceType,
        encounterPaymentDropdownOptions,
    } from "../../../Utils/utilEnums.svelte";
    import { getInsuranceFundById } from "../../../Utils/utilCache.svelte";

    export const syncBillingStatus = async (encounter) => {
        const { billingStatus } = encounter;

        if (billingStatus != 3) return encounter;

        await apiMethods.putMethod
            .updatePatientEncounter({
                ...encounter,
                billingStatus: 2,
            }, false, "Sync billing status")
            .then((res) => {
                if (res.id) encounter = { ...res };
            });

        return encounter;
    };

    export const updateEncounterLatestBillingData = (encounter) => {
        let quoteData = {},
            invoiceData = {};

        if (encounter.billingData?.length)
            encounter.billingData.map((bd) => (bd.isLatestBillingData = false));
        else return encounter;

        if (encounter.billingData.some((bd) => bd.isLatestQuoteData))
            quoteData = encounter.billingData.find(
                (bd) => bd.isLatestQuoteData,
            );

        if (encounter.billingData.some((bd) => bd.isLatestInvoiceData))
            invoiceData = encounter.billingData.find(
                (bd) => bd.isLatestInvoiceData,
            );

        if (isObjectEmpty(invoiceData) && !isObjectEmpty(quoteData))
            quoteData.isLatestBillingData = true;
        else if (isObjectEmpty(quoteData) && !isObjectEmpty(invoiceData))
            invoiceData.isLatestBillingData = true;
        else if (
            !isObjectEmpty(invoiceData) &&
            invoiceData.mbsData?.length > 0 &&
            invoiceData.totalFee != null
        )
            invoiceData.isLatestBillingData = true;
        else quoteData.isLatestBillingData = true;

        // sync latest invoice data oopType with latest quote data oopType
        if (
            !isObjectEmpty(quoteData) &&
            !isObjectEmpty(invoiceData) &&
            quoteData.oopType != invoiceData.oopType
        )
            invoiceData.oopType = quoteData.oopType;

        encounter.billingData = encounter.billingData.map((bd) => {
            if (bd.isLatestQuoteData)
                return {
                    ...bd,
                    isLatestBillingData: quoteData.isLatestBillingData,
                };
            else if (bd.isLatestInvoiceData)
                return {
                    ...bd,
                    isLatestBillingData: invoiceData.isLatestBillingData,
                };
            else return bd;
        });

        return encounter;
    };

    export const generateLatestBillingDetails = async (
        encounter,
        itemType = "",
        billingCodesType = Number(0),
        timeObj = {},
    ) => {
        const isQuote = itemType == "quote";

        const {
            patient,
            skipQuote,
            patientEncounterWorkCoverData,
            patientEncounterTrafficAccidentData,
            scheduledDate,
        } = encounter;

        const { startTime, endTime } = timeObj;

        const billingData = {
            oopType: Number(0),
            isLatestBillingData: Boolean(true),
            skipQuote: skipQuote,
            addGST: Boolean(false),
            insuranceType: Number(patient.insuranceType),
            patientMedicareNo: patient.medicareNo,
            patientMedicareRefNo: patient.medicareRefNo,
            patientMembershipNo: patient.membershipNo,
            patientMembershipRefNo: patient.membershipRefNo,
            patientInsuranceId: patient.insuranceId,
            patientInsurance: patient.insurance,
            patientVeteranNo: patient.veteranNo,
            patientInsuranceState: patient.insuranceState,
        };

        // DVA always set patientPaysTotalFee as FALSE
        if (patient.insuranceType == 20)
            billingData.patientPaysTotalFee = false;

        if (startTime)
            billingData.startDate = `${scheduledDate.split("T")[0]}T${startTime}`;

        if (endTime)
            billingData.endDate = `${scheduledDate.split("T")[0]}T${endTime}`;

        // sync new latest invoice billing data oopType with latest quote data oopType if latest quote data is valid
        if (!isQuote && encounter.billingData?.length) {
            const latestQuoteData = encounter.billingData.find(
                (bd) => bd.isLatestQuoteData,
            );
            if (latestQuoteData) {
                const { oopType } = latestQuoteData;
                billingData.oopType = Number(oopType);
            }
        }

        const insuranceObj = {
            insuranceType:
                billingData.insuranceType == 1 || billingData.insuranceType == 2
                    ? 0
                    : billingData.insuranceType,
            medicareNo: billingData.patientMedicareNo ?? "",
            medicareRefNo: billingData.patientMedicareRefNo ?? "",
            membershipNo: billingData.patientMembershipNo ?? "",
            membershipRefNo: billingData.patientMembershipRefNo ?? "",
        };

        switch (insuranceObj.insuranceType) {
            case 0: // Private
                insuranceObj.insuranceFundId =
                    billingData.patientInsuranceId ?? Number(0);
                insuranceObj.privateCoverType =
                    billingData.patientInsurance ?? "";
                break;
            case 10: // Overseas
                insuranceObj.overseasCover = billingData.patientInsurance ?? "";
                insuranceObj.insuranceFundId =
                    billingData.patientInsuranceId ?? Number(0);
                break;
            case 16: // Workers Compensation
                const workerTempArr = billingData.patientInsurance
                    .replaceAll(" ", "")
                    .split("|");

                if (workerTempArr.length == 4) {
                    insuranceObj.workerEmployer = workerTempArr[2];
                    insuranceObj.workerEmployerEmail = workerTempArr[3];
                    insuranceObj.workerInsurerEmail = "";
                } else if (workerTempArr.length == 3) {
                    insuranceObj.workerEmployer = workerTempArr[0];
                    insuranceObj.workerEmployerEmail = workerTempArr[1];
                    insuranceObj.workerClaimNumber = workerTempArr[2];
                }

                insuranceObj.workerCompensationObj = {
                    ...patientEncounterWorkCoverData,
                };
                break;
            case 17: // Traffic Accident
                insuranceObj.trafficClaimNumber = billingData.patientInsurance;

                insuranceObj.trafficAuthorityObj = {
                    ...patientEncounterTrafficAccidentData,
                };
                break;
            case 20: // DVA
                insuranceObj.veteranNo = billingData.patientVeteranNo;
                break;
            case 25: // Defence (BUPA)
            case 26: // Defence (AMA)
                const defenceTempArr = billingData.patientInsurance
                    .replaceAll(" ", "")
                    .split("|");

                insuranceObj.pidNumber = defenceTempArr[0];
                insuranceObj.danNumber = defenceTempArr[1];

                break;
            case 30: // Public in Private
                insuranceObj.publicInPrivateObj = {
                    ...encounter.patientEncounterPIPData,
                };

                if (insuranceObj.publicInPrivateObj?.email)
                    insuranceObj.pipEmail =
                        insuranceObj.publicInPrivateObj.email;

                if (insuranceObj.publicInPrivateObj?.vendorNumbers)
                    insuranceObj.vendorNumbers =
                        insuranceObj.publicInPrivateObj.vendorNumbers;

                break;
            case 11: // Self insured
            case 15: // Cosmetic
            default:
                break;
        }

        let latestBillingData = syncInsuranceData(
            billingData,
            insuranceObj,
            encounter,
        ).modalData;

        latestBillingData.isLatestQuoteData = Boolean(isQuote);
        latestBillingData.isLatestInvoiceData = Boolean(!isQuote);
        latestBillingData.billingCodesType = billingCodesType;
        latestBillingData.useAsaCodes = billingCodesType == Number(1);

        encounter.billingData = [...encounter.billingData, latestBillingData];

        return encounter;
    };

    export const syncLatestBillingDataTime = (
        latestBillingData,
        timeObj = {},
        scheduledDate,
    ) => {
        // remain latestBillingData if no valid timeObj or scheduledDate
        if (isObjectEmpty(timeObj) || !scheduledDate) return latestBillingData;

        const { startTime, endTime } = timeObj;

        let date = new Date(scheduledDate);

        // remain latestBillingData if invalid date
        if (isNaN(date.getTime())) return latestBillingData;

        const localDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        const formatStartTime = startTime
            ? startTime.length == 5
                ? startTime + ":00"
                : startTime
            : null;
        const formatEndTime = endTime
            ? endTime.length == 5
                ? endTime + ":00"
                : endTime
            : null;

        if (formatStartTime)
            latestBillingData.startDate = `${localDate}T${formatStartTime}`;

        if (formatEndTime) {
            if (startTime && startTime > endTime)
                date.setDate(date.getDate() + 1);

            const adjustedLocalDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            latestBillingData.endDate = `${adjustedLocalDate}T${endTime}`;
        }

        return latestBillingData;
    };

    export const syncInsuranceData = (data, insuranceObj, encounter) => {
        data.patientInsuranceType = insuranceObj.insuranceType;
        data.insuranceType = insuranceObj.insuranceType;

        if (insuranceObj.medicareNo)
            data.patientMedicareNo = insuranceObj.medicareNo;
        if (insuranceObj.medicareRefNo)
            data.patientMedicareRefNo = insuranceObj.medicareRefNo;

        if (insuranceObj.insuranceType != 20)
            data.patientInsurance = insuranceObj.insurance;
        else data.patientVeteranNo = insuranceObj.veteranNo;

        switch (data.patientInsuranceType) {
            case 0:
                data.patientInsuranceId =
                    insuranceObj.insuranceFund?.id ??
                    insuranceObj.insuranceFundId;
                data.patientMembershipNo = insuranceObj.membershipNo;
                data.patientMembershipRefNo = insuranceObj.membershipRefNo
                    ? Number(insuranceObj.membershipRefNo)
                    : null;
                if (insuranceObj.privateCoverType)
                    data.patientInsurance = insuranceObj.privateCoverType;
                break;
            case 10:
                data.patientInsurance = `${insuranceObj.overseasCover}`;
                break;
            case 16:
                data.patientInsurance = `${
                    insuranceObj.workerEmployer ?? ""
                } | ${insuranceObj.workerEmployerEmail ?? ""} | ${
                    insuranceObj.workerClaimNumber
                }`;

                if (insuranceObj.workerCompensationObj?.id)
                    encounter.patientEncounterWorkCoverData = {
                        workCoverAuthority:
                            insuranceObj.workerCompensationObj.id,
                        email: insuranceObj.insurerEmail
                            ? insuranceObj.insurerEmail
                            : insuranceObj.workerCompensationObj?.email,
                        claimId: insuranceObj.workerClaimNumber,
                    };
                break;
            case 17:
                data.patientInsurance = `${insuranceObj.trafficClaimNumber}`;

                if (insuranceObj.trafficAuthorityObj?.id)
                    encounter.patientEncounterTrafficAccidentData = {
                        trafficAccidentAuthority:
                            insuranceObj.trafficAuthorityObj.id,
                        email: insuranceObj.trafficAuthorityEmail
                            ? insuranceObj.trafficAuthorityEmail
                            : insuranceObj.trafficAuthorityObj?.email,
                        claimId: insuranceObj.trafficClaimNumber,
                    };
                break;
            case 20:
                data.patientVeteranNo = `${insuranceObj.veteranNo}`;
                break;
            case 25:
            case 26:
                data.patientInsurance = `${insuranceObj.pidNumber} | ${insuranceObj.danNumber}`;
                break;
            case 30:
                encounter.patientEncounterPIPData = {
                    email: insuranceObj.pipEmail ? insuranceObj.pipEmail : "",
                    vendorNumbers: insuranceObj.vendorNumbers
                        ? insuranceObj.vendorNumbers
                        : "",
                };
                break;
            default:
                break;
        }

        return {
            modalData: data,
            encounterData: encounter,
        };
    };

    export const isSufficientTimeToCreateEstimatesOrInvoices = (encounter) => {
        const { requestedUpdateDetailsDate } = encounter;

        if (!requestedUpdateDetailsDate) return true;

        const now = new Date();
        const time = new Date(requestedUpdateDetailsDate);

        const hoursDiff = (now.getTime() - time.getTime()) / (1000 * 3600);

        return hoursDiff > 24;
    };

    export const isHbfWAFullyCoveredWithPatientPayment = async (encounter, billingData) => {
        //check if private
        if(billingData.patientInsuranceType == 0) { 
            //check if HBF fund
            var fundId = billingData.patientInsuranceId;
            if(fundId){
                let res = await getInsuranceFundById(fundId);
                if(res != null && res.hcpCode == "HBF"){
                    //check if account provider is FullyCovered and state is WA
                    if(encounter.doctorProvider.isFullyCoveredHBFWA == 2 && encounter.doctorProvider.state == "WA") {
                        //check if patient has something to pay
                        if(billingData.patientPaysTotalFee || (!billingData.patientPaysTotalFee && billingData.actualGap > 0)) {
                            //not allowed, return true
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }

    export const isHBFWAProviderChoiceEncounter = async (encounter, billingData) => {
        if(isObjectEmpty(encounter)) return false;
        //check if private
        if(billingData.patientInsuranceType == 0) { 
            //check if HBF fund
            var fundId = billingData.patientInsuranceId;
            if(fundId){
                let res = await getInsuranceFundById(fundId);
                if(res != null && res.hcpCode == "HBF"){
                    //check if account provider is ProviderChoice and state is WA
                    if(encounter.doctorProvider.isFullyCoveredHBFWA == 1 && encounter.doctorProvider.state == "WA") {
                       return true;
                    }
                }
            }
        }

        return false;
    }

    export const isSufficientTimeToNotifyPatient = (encounter) => {
        const { scheduledDate } = encounter;

        if (!scheduledDate) return true;

        const currentDate = new Date();
        const formattedCurrentDate =
            getDate(currentDate, "-", true) + "T00:00:00";

        const givenDate = new Date(scheduledDate);
        const formattedGivenDate = getDate(givenDate, "-", true) + "T00:00:00";

        const now = new Date(formattedCurrentDate);
        const time = new Date(formattedGivenDate);

        const hoursDiff = (now.getTime() - time.getTime()) / (1000 * 3600);

        return hoursDiff > 24;
    };

    export const initTimeObj = (itemProps, timeObj = {}) => {
        const { startDate, endDate, scheduledDate } = itemProps;

        if (!startDate && !endDate) return timeObj;

        if (startDate) {
            timeObj.startTime = startDate.split("T")[1];
            timeObj.startDate = `${scheduledDate.split("T")[0]}T${timeObj.startTime}`;
        }

        if (endDate) {
            timeObj.endTime = endDate.split("T")[1];
            timeObj.endDate = `${scheduledDate.split("T")[0]}T${timeObj.endTime}`;
        }

        return timeObj;
    };

    export const addMinutesToTime = (timeStr, minutes) => {
        const timeParts = timeStr.split(":");
        const hours = parseInt(timeParts[0]);
        const mins = parseInt(timeParts[1]);

        const totalMinutes = hours * 60 + mins + minutes;

        const newHours = Math.floor((totalMinutes / 60) % 24);
        const newMins = totalMinutes % 60;

        const newTimeStr =
            ("0" + newHours).slice(-2) + ":" + ("0" + newMins).slice(-2);

        return newTimeStr;
    };

    export const syncPatientInsuranceDetails = (encounter) => {
        const { billingData, patient } = encounter;

        const validBillingData = getValidBillingData(billingData);

        if (isObjectEmpty(validBillingData)) return patient;

        const {
            patientInsuranceType,
            patientInsuranceState,
            patientInsuranceId,
            patientInsurance,
            patientMedicareNo,
            patientMedicareRefNo,
            patientMembershipNo,
            patientMembershipRefNo,
            patientVeteranNo,
        } = validBillingData;

        return {
            ...patient,
            insuranceType: patientInsuranceType,
            insuranceState: patientInsuranceState,
            insuranceId: patientInsuranceId,
            insurance: patientInsurance,
            medicareNo: patientMedicareNo,
            medicareRefNo: patientMedicareRefNo,
            membershipNo: patientMembershipNo,
            membershipRefNo: patientMembershipRefNo,
            veteranNo: patientVeteranNo,
        };
    };

    export const syncLabourEpiduralPatientTitleAndGender = (
        encounter,
        patient,
    ) => {
        const { typeOfBill } = encounter;

        if (typeOfBill != 2) return patient;

        const { title, sex } = patient;

        if (!title || title == "Title" || title == 20) patient.title = 2; // set Ms as Labour Epidural encounter patient title
        if (sex == 2) patient.sex = 1; // set Female as Labour Epidural encounter patient gender

        return patient;
    };

    export const syncInsuranceDetailsOfLatestBillingData = (encounter) => {
        const { billingData, patient } = encounter;

        if (!billingData || !billingData.length) return encounter;

        const {
            insuranceType,
            insuranceState,
            insuranceId,
            insurance,
            medicareNo,
            medicareRefNo,
            membershipNo,
            membershipRefNo,
            veteranNo,
        } = patient;

        encounter.billingData = billingData.map((bd) => ({
            ...bd,
            insuranceType,
            patientInsuranceType: insuranceType,
            patientInsuranceState: insuranceState,
            patientInsuranceId: insuranceId,
            patientInsurance: insurance,
            patientMedicareNo: medicareNo,
            patientMedicareRefNo: medicareRefNo,
            patientMembershipNo: membershipNo,
            patientMembershipRefNo: membershipRefNo,
            patientVeteranNo: veteranNo,
        }));

        return encounter;
    };

    export const syncEncounterSpecialInsuranceObj = (
        encounter,
        wcData,
        wcObj,
        taData,
        taObj,
        pipObj,
        tppObj,
        defObj,
        insuranceDetails,
    ) => {
        const { patient } = encounter;

        if (patient.insuranceType == 16) {
            encounter.patientEncounterWorkCoverData = {
                workCoverAuthority: wcData?.id ?? Number(0),
                email: wcData?.id
                    ? wcObj?.insurerEmail
                        ? wcObj.insurerEmail
                        : wcData.email
                    : "",
                claimId: wcData?.id ? wcObj.claimNumber : "",
            };

            const { employer, employerEmail } = insuranceDetails;

            encounter.patientEncounterWorkCoverData = {
                ...encounter.patientEncounterWorkCoverData,
                employer: employer ? employer : "",
            };

            encounter.patientEncounterWorkCoverData = {
                ...encounter.patientEncounterWorkCoverData,
                employerEmail: employerEmail ? employerEmail : "",
            };
        }
        // else if (encounter.patientEncounterWorkCoverData)
        //     delete encounter.patientEncounterWorkCoverData;

        if (patient.insuranceType == 17) {
            if (taData?.id)
                encounter.patientEncounterTrafficAccidentData = {
                    trafficAccidentAuthority: taData.id,
                    email: taObj?.email ? taObj.email : taData.email,
                    claimId: taObj.claimNumber,
                };
        }
        // else if (
        //     encounter.patientEncounterTrafficAccidentData
        //         ?.trafficAccidentAuthority
        // )
        //     delete encounter.patientEncounterTrafficAccidentData;

        if (patient.insuranceType == 18)
            encounter.patientEncounterThirdPartyInsurerData = { ...tppObj };
        // else delete encounter.patientEncounterThirdPartyInsurerData;

        if (patient.insuranceType == 25 || patient.insuranceType == 26) {
            encounter.patientEncounterDefenceData = { ...defObj };
        }
        // else delete encounter.patientEncounterDefenceData;

        if (patient.insuranceType == 30)
            encounter.patientEncounterPIPData = {
                email: pipObj.email ? pipObj.email : "",
                vendorNumbers: pipObj.vendorNumbers ? pipObj.vendorNumbers : "",
            };
        // else if (encounter.patientEncounterPIPData?.email)
        //     delete encounter.patientEncounterPIPData;

        return encounter;
    };

    export const resetIrrelevantInsuranceDetails = (
        patientDetails,
        insuranceDetails,
        wcData = {},
        wcObj = {},
        tacData = {},
        tacObj = {},
        pipObj = {},
        tppObj = {},
        defObj = {}
    ) => {
        const { insuranceType } = patientDetails;
        const { insuranceFund } = insuranceDetails;

        if (insuranceType == 0) {
            patientDetails.insuranceState = ""; // ONLY for 16, 17

            if (
                insuranceFund &&
                insuranceFund.id &&
                insuranceFund.eclipseCode.toUpperCase() !== "AMA" &&
                insuranceFund.eclipseCode.toUpperCase() !== "AUH"
            )
                delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            tacData = null;
            tacObj = {};

            delete patientDetails.veteranNo;

            pipObj = {};
            defObj = {};

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;

            tppObj = {};
        } else if (insuranceType == 10) {
            patientDetails.insuranceState = ""; // ONLY for 16, 17

            delete patientDetails.medicareNo;
            delete patientDetails.medicareRefNo;
            delete insuranceDetails.privateCoverType;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            tacData = null;
            tacObj = {};

            delete patientDetails.veteranNo;

            pipObj = {};
            defObj = {};

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;

            tppObj = {};
        } else if (insuranceType == 11 || insuranceType == 15) {
            patientDetails.insuranceState = ""; // ONLY for 16, 17

            delete insuranceDetails.membershipNo;
            delete insuranceDetails.membershipRefNo;
            delete insuranceDetails.insuranceFund;
            delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            tacData = null;
            tacObj = {};

            delete patientDetails.veteranNo;

            pipObj = {};
            defObj = {};

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;

            tppObj = {};
        } else if (insuranceType == 16) {
            delete insuranceDetails.membershipNo;
            delete insuranceDetails.membershipRefNo;
            delete insuranceDetails.insuranceFund;
            delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            tacData = null;
            tacObj = {};

            delete patientDetails.veteranNo;

            pipObj = {};
            defObj = {};

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;

            tppObj = {};
        } else if (insuranceType == 17) {
            delete insuranceDetails.membershipNo;
            delete insuranceDetails.membershipRefNo;
            delete insuranceDetails.insuranceFund;
            delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            delete patientDetails.veteranNo;

            pipObj = {};
            defObj = {};

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;

            tppObj = {};
        } else if (insuranceType == 18) {
            patientDetails.insuranceState = ""; // ONLY for 16, 17

            delete patientDetails.medicareNo;
            delete patientDetails.medicareRefNo;
            delete insuranceDetails.membershipNo;
            delete insuranceDetails.membershipRefNo;
            delete insuranceDetails.insuranceFund;
            delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            tacData = null;
            tacObj = {};
            defObj = {};

            delete patientDetails.veteranNo;

            pipObj = {};

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;
        } else if (insuranceType == 20) {
            patientDetails.insuranceState = ""; // ONLY for 16, 17

            delete patientDetails.medicareNo;
            delete patientDetails.medicareRefNo;
            delete insuranceDetails.membershipNo;
            delete insuranceDetails.membershipRefNo;
            delete insuranceDetails.insuranceFund;
            delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            tacData = null;
            tacObj = {};

            pipObj = {};
            defObj = {};

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;

            tppObj = {};
        } else if (insuranceType == 25 || insuranceType == 26) {
            patientDetails.insuranceState = ""; // ONLY for 16, 17

            delete insuranceDetails.membershipNo;
            delete insuranceDetails.membershipRefNo;
            delete insuranceDetails.insuranceFund;
            delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            tacData = null;
            tacObj = {};

            delete patientDetails.veteranNo;

            pipObj = {};

            tppObj = {};
        } else if (insuranceType == 30) {
            patientDetails.insuranceState = ""; // ONLY for 16, 17

            delete patientDetails.medicareNo;
            delete patientDetails.medicareRefNo;
            delete insuranceDetails.membershipNo;
            delete insuranceDetails.membershipRefNo;
            delete insuranceDetails.insuranceFund;
            delete insuranceDetails.privateCoverType;

            delete insuranceDetails.overseasCover;

            wcData = null;
            wcObj = {};
            delete insuranceDetails.employer;
            delete insuranceDetails.employerEmail;

            tacData = null;
            tacObj = {};
            defObj = {};

            delete patientDetails.veteranNo;

            delete insuranceDetails.pidNumber;
            delete insuranceDetails.danNumber;

            tppObj = {};
        }

        return {
            patientDetails,
            insuranceDetails,
            wcData,
            wcObj,
            tacData,
            tacObj,
            pipObj,
            tppObj,
            defObj
        };
    };

    export const getEncounterPayments = (encounter, { statuses = null, paidBy = null } = {}) => {
        let payments = [];

        if (encounter.estimates?.length > 0) {
            encounter.estimates.forEach((estimate) => {
                if (estimate.payments?.length > 0) {
                    payments.push(...estimate.payments);
                }
            });
        }

        if (encounter.invoices?.length > 0) {
            encounter.invoices.forEach((invoice) => {
                if (invoice.payments?.length > 0) {
                    payments.push(...invoice.payments);
                }
            });
        }

        if (payments.length > 0) {
            try {
                payments.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
            } catch (e) {
                console.warn("Error sorting payments", e);
            }
        }

        if (statuses) {
            payments = payments.filter((p) => statuses.includes(p.status));
        }

        if (paidBy !== null && paidBy !== undefined) {
            payments = payments.filter((p) => p.paidBy === paidBy);
        }

        return payments;
    }

    export const syncInvoiceCalculationAmountBasedOnPayments = async (
        latestQuoteData,
        latestInvoiceData,
        estimates = [],
        reloadFunc = () => {},
    ) => {
        if (!latestInvoiceData || isObjectEmpty(latestInvoiceData)) return;

        const { oopType, patientPaysTotalFee } = latestQuoteData;

        if (oopType == null || oopType != 0 || !estimates.length) return;

        const lastEstimate = estimates[estimates.length - 1];

        const { payments, totalFee, actualGap } = lastEstimate;

        const { calculationAmount: invoiceCalculationAmount } =
            latestInvoiceData;

        if (
            !payments ||
            !payments.length ||
            !payments.some(({ paidBy }) => paidBy == 0)
        )
            return;

        const receivedPaymentsSum = payments
            .filter(
                ({ paidBy, status }) =>
                    paidBy == 0 && (status == 1 || status == 2),
            )
            .reduce((sum, { amount }) => sum + amount, 0);

        const fullyPaid = receivedPaymentsSum == totalFee;

        if (!fullyPaid) return;

        const refundedPaymentsSum = payments
            .filter(({ paidBy, status }) => paidBy == 0 && status == 5)
            .reduce((sum, { amount }) => sum + amount, 0);

        if (refundedPaymentsSum <= 0) return;

        if (patientPaysTotalFee) {
            const newTotalFee = totalFee - refundedPaymentsSum;

            if (newTotalFee == invoiceCalculationAmount) return;

            latestInvoiceData.calculationAmount = newTotalFee;
            latestInvoiceData.billingType = 5; //force back to total fee 
        } else {
            const newGapFee = actualGap - refundedPaymentsSum;

            if (newGapFee == invoiceCalculationAmount) return;

            latestInvoiceData.calculationAmount = newGapFee;
            latestInvoiceData.billingType = 3; //force back to gap fee if user changed
        }

        reloadFunc(latestInvoiceData, true, "invoice");
    };

    export const checkPatientDetailsChange = (
        backupDetails,
        patientDetails,
    ) => {
        if (
            !backupDetails ||
            !patientDetails ||
            isObjectEmpty(backupDetails) ||
            isObjectEmpty(patientDetails)
        )
            return false;

        for (let key in backupDetails) {
            if (
                !patientDetails.hasOwnProperty(key) ||
                backupDetails[key] != patientDetails[key]
            )
                return true;
        }

        return false;
    };

    export const checkPatientOpvFail = (res) => {
        return (
            (res.healthFundStatus != 0 && res.healthFundStatus != 1) ||
            (res.medicareStatus != 0 && res.medicareStatus != 1) ||
            res.suggestedMedicareNum ||
            res.suggestedMedicareReference ||
            res.suggestedName ||
            res.suggestedLastName ||
            res.suggestedBirthdate ||
            res.suggestedInsuranceId ||
            res.veteranStatus
        );
    };

    export const getSumOfColumnValues = (arr = [], addGST = false) => {
        let rebateBasicUnits = 0,
            benefit100 = 0,
            rebate = 0,
            feeCharged = 0,
            amaUnits = 0;

        for (let i = 0; i < arr.length; i++) {
            // rebateBasicUnits += arr[i].rebateBasicUnits;
            rebateBasicUnits += getMBSRebateUnits(arr[i]);
            benefit100 += arr[i].benefit100;
            rebate += arr[i].rebate;
            feeCharged += arr[i].feeCharged;
            amaUnits += arr[i].fieldChange && arr[i].fieldChange > 0 ? arr[i].fieldChange : arr[i].amaUnits;
        }

        const _getAmountDisplayText = (amount, addGST) => {
            if (!addGST || !amount) return getAmountInCurrency(amount);

            return `${getAmountInCurrency(amount)} + ${getAmountInCurrency(amount / 10)} GST = ${getAmountInCurrency(amount * 1.1)} Total`;
        };

        let sumObj = {
            isLast: true,
            itemCode: "Total",
            itemNum: "Total",
            infoType: "",
            infoShortTitle: "",
            mbsItemNum: "",
            rebateBasicUnits,
            amaUnits,
            benefit100: getAmountInCurrency(benefit100),
            rebate: getAmountInCurrency(rebate),
            feeCharged: _getAmountDisplayText(feeCharged, addGST),
        };

        return sumObj;
    };

    export const getDisplayBillingRuleText = (obj = {}) => {
        const { billingType, calculationAmount, withinFundRules } = obj;

        if (!billingType) return `Billing rule not selected`;

        const billingTypeInPercentage = getBillingType(billingType)
            .toString()
            .toLowerCase()
            .includes("percentage");

        return `Billing rule: ${getBillingType(billingType)} of ${
            billingTypeInPercentage
                ? `${Number(calculationAmount).toFixed(2)}%`
                : getAmountInCurrency(calculationAmount)
        } ${withinFundRules ? "" : "(outside fund rules)"}`;
    };

    export const getDisplayPaymentDetailsText = (obj = {}) => {
        const { patientPaysTotalFee, actualGap, totalFee, oopType, rebate } =
            obj;

        if (!patientPaysTotalFee) {
            if (!actualGap)
                return `No gap. Charging ${getAmountInCurrency(totalFee)} to Insurer`;
            else
                return `${getOOPType(oopType)} patient pays ${getAmountInCurrency(actualGap)}, insurer pays ${getAmountInCurrency(rebate)}, total ${getAmountInCurrency(totalFee)}`;
        } else
            return `${getOOPType(oopType)} patient pays total fee ${getAmountInCurrency(totalFee)}`;
    };

    export const syncEncounterLatestNote = async (encounters = []) => {
        if (!encounters.length) return [];

        let hasNoteEncounterIds = [];

        encounters.map(({ id, hasNotes }) => {
            if (hasNotes && !hasNoteEncounterIds.includes(id))
                hasNoteEncounterIds.push(id);
        });

        if (!hasNoteEncounterIds.length) return encounters;

        let relatedNotes = [];

        await apiMethods.getMethod
            .getMultipleEncounterNotes(hasNoteEncounterIds)
            .then((res) => (relatedNotes = [...res.results]));

        relatedNotes = relatedNotes
            .sort((a, b) => new Date(b.updatedDate) - new Date(a.updatedDate))
            .reduce((acc, curr) => {
                const existObj = acc.some((obj) => obj.relId == curr.relId);
                if (!existObj) acc.push(curr);
                return acc;
            }, []);

        encounters.map((encounter) => {
            encounter.ltsNote = encounter.hasNotes
                ? relatedNotes.find((note) => note.relId == encounter.id)
                : null;

            return encounter;
        });

        return encounters;
    };

    export const updateBillingRuleTotalFee = async (
        encounter = {},
        latestObj = {},
    ) => {
        const { id, doctorId } = encounter;
        const { useAsaCodes, mbsData, asaData } = latestObj;

        const obj = {
            accountId: doctorId,
            patientEncounterId: id,
            patientEncounterBillingDataId: latestObj.id,
            gap: latestObj.actualGap,
            totalFee: latestObj.totalFee,
            insurance: latestObj.rebate,
            withinFundRules: latestObj.withinFundRules,
            calculationAmount: latestObj.calculationAmount // for insurance [20, 25] does not support select billing rule, calculationAmount is 0
                ? parseFloat(latestObj.calculationAmount.toFixed(2))
                : 0,
            billingType: latestObj.billingType ?? 0,
            oopType: latestObj.oopType,
            patientPaysTotalFee: latestObj.patientPaysTotalFee,
            usingAmaRates: latestObj.usingAmaRates,
            mbsItemsFeeCharges: [],
            asaItemsFeeCharges: [],
        };

        let itemsFeeCharges = [];

        if (useAsaCodes) {
            asaData.map((data) => {
                itemsFeeCharges.push({
                    itemIdentifier: data.itemCode,
                    subItemIdentifier: data.subItemCode,
                    feeCharged: data.feeCharged,
                    rebate: data.rebate,
                    fieldChange: data.fieldChange,
                    units: data.amaUnits,
                });
            });
            obj.asaItemsFeeCharges = [...itemsFeeCharges];
        } else {
            mbsData.map((data) => {
                itemsFeeCharges.push({
                    itemIdentifier: data.itemNum.toString(),
                    subItemIdentifier: data.subItemNum.toString(),
                    feeCharged: data.feeCharged,
                    rebate: data.rebate,
                    fieldChange: data.fieldChange,
                    // units: data.rebateBasicUnits,
                    units: getMBSRebateUnits(data),
                });
            });
            obj.mbsItemsFeeCharges = [...itemsFeeCharges];
        }

        await apiMethods.postMethod.updateBillingRuleTotalFee(obj);
    };

    export const copyQuoteItemsToInvoice = async (
        encounter,
        callback = () => {},
    ) => {
        const { billingData } = encounter;

        // override billing data and items if exists
        let latestInvoiceData = billingData.find(
            (bd) => bd.isLatestInvoiceData,
        );

        if (!latestInvoiceData) {
            await generateLatestBillingDetails(encounter, "invoice");
            latestInvoiceData = encounter.billingData.find(
                (bd) => bd.isLatestInvoiceData,
            );

            if(!latestInvoiceData) {
                return encounter;
            }
        };

        const latestQuoteData = billingData.find((bd) => bd.isLatestQuoteData);

        if(latestQuoteData.billingCodesType == 2) {
            //free text items
            //copy items to invoice
            var invoiceFreeTextItems = [];
            if(latestQuoteData.freeTextData && latestQuoteData.freeTextData.length) {
                latestQuoteData.freeTextData.map((item) => {
                    var clonedItem = {...item};
                    clonedItem.id = 0;
                    invoiceFreeTextItems.push(clonedItem);
                });
                latestInvoiceData.freeTextData = [...invoiceFreeTextItems];
            } else
                latestInvoiceData.freeTextData = [];
            

            //other billing details
            latestInvoiceData.billingCodesType = latestQuoteData.billingCodesType;
            latestInvoiceData.oopType = latestQuoteData.oopType;
            latestInvoiceData.patientPaysTotalFee = latestQuoteData.patientPaysTotalFee;

            latestInvoiceData.totalFee = latestQuoteData.totalFee;
            latestInvoiceData.billingType = latestQuoteData.billingType;
            latestInvoiceData.actualGap = latestQuoteData.actualGap;
            latestInvoiceData.calculationAmount = latestQuoteData.calculationAmount;

        } else {
            //mbs or asa items
             //check if there is a time code added on invoice and if start and end time are entered
            let invoiceTimeCode = null;
            if(latestInvoiceData.startDate && latestInvoiceData.endDate){
                if(latestInvoiceData.mbsData && latestInvoiceData.mbsData.length > 0) {
                    invoiceTimeCode = latestInvoiceData.mbsData.find((mbs) => mbs.infoType == "Time");
                }
            } else {
                //no start end date, but time selected
                if(latestInvoiceData.mbsData && latestInvoiceData.mbsData.length > 0) {
                    invoiceTimeCode = latestInvoiceData.mbsData.find((mbs) => mbs.infoType == "Time");
                }
            }

            latestInvoiceData = {
                ...latestQuoteData,
                //keep start and end time if already provided
                startDate: latestInvoiceData.startDate,
                //oopType: latestInvoiceData.oopType, //Maybe it's needed?: waiting response.
                endDate: latestInvoiceData.endDate,
                id: latestInvoiceData.id,
                isLatestQuoteData: false,
                isLatestInvoiceData: true,
                isLatestBillingData: latestInvoiceData.isLatestBillingData,
            };

            // remove Time item from mbs item list as invoice time item saved separately
            if (!latestQuoteData.useAsaCodes && latestInvoiceData.mbsData){
                latestInvoiceData.mbsData = latestInvoiceData.mbsData.filter(
                    (mbs) => mbs.infoType != "Time",
                );
                //add preexisting invoice mbs time code
                if(invoiceTimeCode){
                    latestInvoiceData.mbsData.push(invoiceTimeCode);
                }
            }
        }

        encounter.billingData = encounter.billingData.map((bd) => {
            if (bd.id == latestInvoiceData.id) return latestInvoiceData;
            return bd;
        });

        await apiMethods.putMethod
            .updatePatientEncounter(encounter, true, "Copy quote items to invoice")
            .then((res) => {
                if (res.id) encounter = { ...res };
            });

        await callback(false, encounter);

        return encounter;
    };

    export const getOcrText = (date) => {
        const [dateArr, timeStr] = date.split("T");

        const dateStr = `${parseInt(dateArr.split("-")[2])}/${parseInt(
            dateArr.split("-")[1],
        )}/${dateArr.split("-")[0].slice(-2)}`;

        const time = timeStr.slice(0, 5);

        return `Imported via OCR on ${dateStr} at ${time}`;
    };

    export const billingRuleAllowed = (latestBillingData = {}) => {
        const { insuranceType, usingAmaRates, useAsaCodes } = latestBillingData;

        if (insuranceTypesNotAllowBillingRule.includes(insuranceType))
            return false;

        if (insuranceTypesMayAllowAmaBillingRule.includes(insuranceType)) {
            // for defence where we don't use Ama rates, hide selector
            if (insuranceType == 26 && !usingAmaRates) return false;

            if ([16, 17].includes(insuranceType)) {
                // for WC or TAC, do not allow select billing rule with MBS codes and not Ama rates
                // preselect billing rule to 3
                if (!useAsaCodes && !usingAmaRates) return false;
            }
        }

        return true;
    };

    export const billingRuleUnitValueAndNoGapProvider = (
        latestObj,
        doctorProvider,
        fundData,
    ) => {
        const { billingType, insuranceType } = latestObj;
        const { isNoGapBupa, isNoGapHCF, isNoGapNib } = doctorProvider;
        const { healthFundName } = fundData;

        // unit value rule && private insurance && selected provider does no support selected fund with no gap
        return (
            billingType == 2 &&
            insuranceType == 0 &&
            ((healthFundName == "BUP" && isNoGapBupa) ||
                (healthFundName == "HCF" && isNoGapHCF))
            // || (healthFundName == "NIB" && isNoGapNib)
        );
    };

    export const getMatchedSurgeon = (
        surgeon = {},
        hospital = {},
        doctorProvider = {},
        patientDetails = {},
        surgeonOptions = [],
        encounters = [],
    ) => {
        if (!surgeon || isObjectEmpty(surgeon)) return null;

        const { providerUniqueId } = surgeon;

        const filteredSurgeonFromAccountDoctors = surgeonOptions.length
            ? surgeonOptions
                  .filter((s) => s.providerUniqueId == providerUniqueId)
                  .map((s) => ({ ...s, matchingId: `${s.id}${s.state}` }))
            : get(accountDoctors)
                  .filter((s) => s.providerUniqueId == providerUniqueId)
                  .map((s) => ({ ...s, matchingId: `${s.id}${s.state}` }));

        // in this case no other potential surgeon from multiple states, return selected surgeon
        if (filteredSurgeonFromAccountDoctors?.length == 1) return surgeon;

        //sync with hospital state if its available and return matched surgeon which state is same as selected hospital state
        if (hospital && !isObjectEmpty(hospital)) {
            const { state: hospitalState } = hospital;

            if (
                filteredSurgeonFromAccountDoctors.some(
                    (s) =>
                        hospitalState &&
                        s.state.toLowerCase() == hospitalState.toLowerCase(),
                )
            )
                return filteredSurgeonFromAccountDoctors.find(
                    (s) => s.state.toLowerCase() == hospitalState.toLowerCase(),
                );
        }

        // sync with doctor provider state if its available and return matched surgeon which state is same as selected doctor provider state
        if (doctorProvider && !isObjectEmpty(doctorProvider)) {
            const { state: doctorProviderState } = doctorProvider;

            if (
                filteredSurgeonFromAccountDoctors.some(
                    (s) =>
                        doctorProviderState &&
                        s.state.toLowerCase() ==
                            doctorProviderState.toLowerCase(),
                )
            )
                return filteredSurgeonFromAccountDoctors.find(
                    (s) =>
                        s.state.toLowerCase() ==
                        doctorProviderState.toLowerCase(),
                );
        }

        // sync with calendar nested encounters which has state and return matched surgeon which state is same as selected calendar nested encounters state
        if (encounters.length) {
            const encounterWithHospitalState = encounters.find(
                (e) => e.hospital && !isObjectEmpty(e.hospital),
            );

            if (
                encounterWithHospitalState &&
                !isObjectEmpty(encounterWithHospitalState)
            ) {
                const hospitalState = encounterWithHospitalState.hospital.state;

                if (
                    filteredSurgeonFromAccountDoctors.some(
                        (s) =>
                            hospitalState &&
                            s.state.toLowerCase() ==
                                hospitalState.toLowerCase(),
                    )
                )
                    return filteredSurgeonFromAccountDoctors.find(
                        (s) =>
                            s.state.toLowerCase() ==
                            hospitalState.toLowerCase(),
                    );
            }
        }

        // sync with patient insurance state if its available and return matched surgeon which state is same as selected patient insurance state
        if (patientDetails && !isObjectEmpty(patientDetails)) {
            const { insuranceState: patientInsuranceState } = patientDetails;

            if (
                filteredSurgeonFromAccountDoctors.some(
                    (s) =>
                        patientInsuranceState &&
                        s.state.toLowerCase() ==
                            patientInsuranceState.toLowerCase(),
                )
            )
                return filteredSurgeonFromAccountDoctors.find(
                    (s) =>
                        s.state.toLowerCase() ==
                        patientInsuranceState.toLowerCase(),
                );
        }

        return { ...surgeon, matchingId: `${surgeon.id}${surgeon.state}` };
    };

    export let loadActivityAndEncounterLogs = async (encounterId, extended = false) => {
        const res =
            await apiMethods.getMethod.getActivityAndEncounterLogsById(
                encounterId, extended
            );

        if (!res || res.error) {
            showError(res.error);
            return {
                error:
                    res.error ??
                    "Failed to load encounter logs, please refresh the page and try again and contact support if the issue persists.",
            };
        }

        return {
            eclipseLogs: res.eclipseLogs,
            activityLogs: res.activityLogs,
            totalEclipseLogs: res.totalEclipseLogs,
            totalActivityLogs: res.totalActivityLogs
        };
    };

    export const getEncounterMissingProperties = (encounter = {}) => {
        if (!encounter || isObjectEmpty(encounter)) return [];

        const {
            doctorId,
            doctorProviderId,
            surgeonId,
            surgeonProviderId,
            patient,
        } = encounter;

        const res = [];

        if (!doctorId || doctorId <= 0) res.push("Missing doctor");

        if (!doctorProviderId || doctorProviderId <= 0)
            res.push("Missing doctor provider number");

        if (!surgeonId || surgeonId <= 0) res.push("Missing surgeon");

        if (!surgeonProviderId || surgeonProviderId <= 0)
            res.push("Missing surgeon provider number");

        if (!patient || isObjectEmpty(patient))
            res.push("Missing Patient Details");
        else {
            const {
                insuranceType,
                medicareNo,
                medicareRefNo,
                insuranceId,
                membershipNo,
            } = patient;

            // Private or Self-Insured
            if ([0, 11].includes(insuranceType)) {
                if (!medicareNo) res.push("Missing Medicare number");

                if (!medicareRefNo)
                    res.push("Missing Medicare reference number");
            }

            // Private check insuranceFund && membershipNo
            if (insuranceType == 0) {
                if (!insuranceId || insuranceId <= 0)
                    res.push("Missing private insurance fund name");

                if (!membershipNo)
                    res.push("Missing private insurance member number");
            }
        }

        return res;
    };

    export const getMBSRebateUnits = (data) => {
        if (!data || isObjectEmpty(data)) return 0;

        const { rebateBasicUnits, fieldChange } = data;

        return fieldChange && fieldChange > 0 ? fieldChange : rebateBasicUnits;
    };

    export const getASAAmaUnits = (data) => {
        if (!data || isObjectEmpty(data)) return 0;

        const { amaUnits, fieldChange } = data;

        return fieldChange && fieldChange > 0 ? fieldChange : amaUnits;
    };

    export const getFormatOpvFailLogs = (logsData) => {
        const response = logsData[0].response
            ? JSON.parse(logsData[0].response)
            : {};

        if (
            response &&
            (response.hasOwnProperty("medicareStatus") ||
                response.hasOwnProperty("healthFundStatus"))
        );
        else if (response && (response.code || response.message)) {
            response.medicareStatus = {
                status: {
                    code: response.code,
                    text: response.message,
                },
            };
            response.healthFundStatus = {
                status: {
                    code: response.code,
                    text: response.message,
                },
            };
        } else {
            response.medicareStatus = {
                status: {
                    code: "-",
                    text: "Unknown error",
                },
            };
            response.healthFundStatus = {
                status: {
                    code: "-",
                    text: "Unknown error",
                },
            };
        }

        return response;
    };

    export const enablePatientClaimForEncounter = (encounter) => {
        if (!encounter || isObjectEmpty(encounter)) return false;

        const {
            billingData,
            claimTransactionId,
            patientClaim,
            patient: { birthDate, insuranceType },
        } = encounter;

        // Always enable patient claim for encounter if patientClaim is true
        if (patientClaim) return true;

        /*
            Flag to show patient claim for encounter if:
            1. has NO claimTransactionId
            2. has valid latest billing data (latestInvoiceBillingData > latestQuoteBillingData) with patientPaysTotalFee is TRUE
            3. patient age is >= 14
            4. patient insurance type is  either Private or Self-Insured
        */

        if (!!claimTransactionId) return false;

        if (!billingData || !billingData.length) return false;

        const patientAge = getPatientAge(birthDate);

        return (
            patientAge >= 14 &&
            [
                DirectInsuranceType.Private,
                DirectInsuranceType.SelfInsured,
            ].includes(insuranceType)
        );
    };

    export const checkOpvContainsReciprocalHealthCareAgreement = async (
        encounterId,
    ) => {
        if (!encounterId) return false;

        let eclipseLogs = [];

        await apiMethods.getMethod
            .getActivityAndEncounterLogsById(encounterId)
            .then((res) => {
                if (!res || res.error) return;
                if (res.eclipseLogs && res.eclipseLogs.length)
                    eclipseLogs = [...res.eclipseLogs];
            });

        if (!eclipseLogs.length) return false;

        return (
            !!eclipseLogs[0].response &&
            String(eclipseLogs[0].response)
                .toLowerCase()
                .includes("reciprocal health care agreement")
        );
    };

    export const syncEncounterPaymentDropdownOptionFilter = (
        params,
        dropdownId,
    ) => {
        const _resetFilter = (params) => {
            encounterPaymentDropdownOptions.map((id) => {
                const propertyName = getEncounterPaymentDropdownOption(
                    id,
                    "apiFilter",
                );
                delete params[propertyName];
            });

            return params;
        };

        params = _resetFilter(params);

        if (dropdownId) {
            const propertyName = getEncounterPaymentDropdownOption(
                dropdownId,
                "apiFilter",
            );
            params[propertyName] = 1;
        }

        return params;
    };

    const insuranceTypesNoQuotingApplied = [16, 17, 20, 25, 26, 99, 30];

    const defaultBillingStatus = (insuranceTypeId) => {
        if (insuranceTypesNoQuotingApplied.includes(insuranceTypeId))
            return 2; //ready
        else
            return 0; //pre-quote
    }

    export const updatedBillingStatus = (status, oldInsType, newInstType) => {
        //insurance type did not changes
        if(oldInsType && oldInsType == newInstType) return status;
        let oldDefaultStatus = defaultBillingStatus(oldInsType);
        //if status is not default, cannot change
        if(status != oldDefaultStatus) return status;
        return defaultBillingStatus(newInstType);
    }

    export const getDelayHoursAfterSentDate = (invoice, delayHour = 24) => {

        const roundUpToNextHour10 = (date) => {
             const t = new Date(date);           // don't mutate the input
            const candidate = new Date(t);

            // candidate = this hour at :minutePast
            candidate.setMinutes(10, 0, 0);

            // If we're at/before HH:minutePast, use it; otherwise go to next hour's :minutePast
            if (t <= candidate) return candidate;

            candidate.setHours(candidate.getHours() + 1);
            return candidate;
        }

        if(!invoice) return;

        const { sentDate } = invoice;

        const date = new Date(sentDate);

        if (isNaN(date.getTime())) return "Invalid date";

        date.setHours(date.getHours() + delayHour);

        return getDateFromDateString(roundUpToNextHour10(date));
    };

    export const showEncounterPendingClaimTooltip = (encounter) => {
        if (!encounter) return false;

        
        var billingData = getValidBillingData(encounter.billingData);
        var insuranceType =  isObjectEmpty(billingData)
            ? encounter.patient.insuranceType
            : getValidBillingData(encounter.billingData).insuranceType;
        
        if(insuranceType != 0 && insuranceType != 11 && insuranceType != 20) {
            //show only if: Insurance type private or self insured or DVA
            return false;
        }

        if(billingData.patientPaysTotalFee && !billingData.patientClaim) {
            //show only if: patientPaysTotalFee = false or (patientPaysTotalFee =true and Patient claim = true)
            return false;
        }

        let invoiceSent = false;
        let patientClaimIsOn = encounter?.patientClaim;
        if(encounter.invoices && encounter.invoices.length > 0){
            var invoice = encounter.invoices.find((i) => i.status == 2); //invoice sent
            if(invoice){
                invoiceSent = true;
                patientClaimIsOn = invoice.patientClaimSnapshot;
            }
        }

        const patientBillingStatusIsComplete = encounter?.billingStatus == 5;

        return (
            encounter?.claimStatus == 0 && invoiceSent &&
            (patientClaimIsOn || !patientBillingStatusIsComplete) &&
            !encounter.claimTransactionId
        );
    };

    export const showEncounterRemoveClaimDelayIcon = (encounter) => {
        if (!encounter) return false;

        const patientClaimIsOn = encounter?.patientClaim;
        const patientBillingStatusIsComplete = encounter?.billingStatus == 5;
        const patientBillingStatusIsSubmittedOrInProgress = [3, 4].includes(
            encounter?.billingStatus,
        );

        return (
            !!encounter.claimDelayHours &&
            (patientClaimIsOn
                ? patientBillingStatusIsComplete
                : patientBillingStatusIsSubmittedOrInProgress)
        );
    };

    export const getEncounterClaimStatusTooltip = (encounter) => {
        if (!encounter) return "";

        const encounterSentInvoice = encounter.invoices && encounter.invoices.length > 0 ?
            encounter.invoices.find((i) => i.status == 2) : null;

        const patientClaimIsOn = encounter?.patientClaim;
        const patientBillingStatusIsComplete = encounter?.billingStatus == 5;
        const patientBillingStatusIsSubmittedOrInProgress = [3, 4].includes(
            encounter?.billingStatus,
        );
        const claimDelayHours = encounter?.claimDelayHours ?? 24;

        if (patientClaimIsOn)
            return patientBillingStatusIsComplete
                ? `The claim is estimated to run on ${getDelayHoursAfterSentDate(encounterSentInvoice, claimDelayHours)}`
                : `Patient claim is active for this encounter. The system will automatically claim it ${claimDelayHours} hours after the encounter is fully paid and complete.`;
        else
            return patientBillingStatusIsSubmittedOrInProgress
                ? `The claim is estimated to run on ${getDelayHoursAfterSentDate(encounterSentInvoice, claimDelayHours)}`
                : `The system will automatically claim this encounter ${claimDelayHours} hours after the encounter is submitted.`;
    };

    export const getConsultTimeItemNote = (item, itemProps) => {
        const { itemType, billingType, startDate, endDate, hospital, surgeon } =
            itemProps;

        // set default consult time item note for Consult & Attendance Invoice ONLY
        if (itemType != "invoice" || billingType != 1)
            return item?.encounterItemDescription ?? "";

        if (item.encounterItemDescription) return item.encounterItemDescription;

        const { infoType, infoShortTitle, infoRegion } = item;

        if (infoType != "Consults" || (infoType == "Consults" && infoRegion != "Referred")) return item.encounterItemDescription ?? "";

        if(!surgeon) {
            return "Rounds in Hospital";
        }

        const withinHospitalText = `Referral ${hospital?.id ? "within" : "outside"} hospital`;

        if (!startDate || !endDate) return withinHospitalText;

        const timeRegex =
            /(\d+\s*min|\d+\s*-\s*\d+\s*min|\d+\+\s*min|\d+\s*hours?)/i;

        return timeRegex.test(infoShortTitle)
            ? `${withinHospitalText} ${getHHMM(startDate)} - ${getHHMM(endDate)}`
            : "";
    };

    export const getEncounterAdjustScheduledDate = (
        encounter,
        prevEncounter,
    ) => {
        const { scheduledDate } = encounter;
        const prevScheduledDate = new Date(prevEncounter.scheduledDate);

        if (
            getTimeDiffInMillSec(new Date(scheduledDate), prevScheduledDate) <
            FallBackEncounterDurationMins * 60 * 1000
        )
            return addHoursToDate(
                prevScheduledDate,
                FallBackEncounterDurationMins / 60,
            );

        return scheduledDate;
    };

    export const filterSurgeonResults = (surgeons = []) => {
        const res = [];

        surgeons.forEach((sur) => {
            sur.uniqueId = sur.uniqueId ?? sur.providerUniqueId;
            if (
                !res.some(
                    (filteredSurgeon) =>
                        filteredSurgeon.uniqueId === sur.uniqueId,
                )
            )
                res.push(sur);
        });

        return res;
    };

    /**
     * @param {Object} props
     * @param {number} props.encounterId
     * @param {string} props.type - side panel type (view/edit/duplicate)
     * @param {function} props.onCloseFunction - what to do after close side panel
     * @returns {void}
     */
    export const openViewEncounterSidePanel = (
        props = { encounterId, type, onCloseFunction: () => {}, createProps },
    ) => {
        const { encounterId, type = "view", onCloseFunction, createProps } = props;

        if (!encounterId || !["view", "edit", "duplicate"].includes(type)) {
            if(type != "create") return; //for create other params are needed
        }

        const sidePanelProps = get(EncounterSidePanelProps);

        sidePanelProps.id = encounterId;
        sidePanelProps.type = type;
        sidePanelProps.createProps = createProps || null;
        sidePanelProps.onCloseFunction = onCloseFunction;

        EncounterSidePanelProps.set({ ...sidePanelProps });
    };

    export const closeViewEncounterSidePanel = () => {
        EncounterSidePanelProps.set({
            id: null,
            type: "",
            shouldTriggerCloseFunction: false,
            onCloseFunction: () => {},
        });
    };

    export const _getDisplayDate = (date) => {
        if (!date) return "";

        const formatDate = getFormatDateAndTime(date);

        const formatDateArr = formatDate.split(", ");

        return formatDateArr[1] == "00:00" ? formatDateArr[0] : formatDate;
    };

    export const _getDoctorFullName = (doctorDetails) => {
        if(!doctorDetails) return "";
        
        const { firstName, lastName, title } = doctorDetails;

        return `${getTitle(title)} ${firstName} ${lastName}`;
    };

    export const _getSurgeonFullName = (surgeonDetails) => {
        if(!surgeonDetails) return "";

        const { firstName, lastName, title } = surgeonDetails;

        return `${getTitle(title)} ${firstName} ${lastName}`;
    };

    const colorEnum = {
        gray: "#B2BAC9",
        blue: "#34ABD1",
        pink: "#FF00A8",
        purple: "#6B48FF",
        green: "#2CC09C",
    };

    export const getPatientIcon = (encounter, encounterUnderOrOverPayment) => {
        let iconColor = colorEnum.gray;
        let patientIconTooltip, patientTooltipColor;

        const underpaymentIcon = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 25C19.4005 25 25 19.3889 25 12.5C25 5.59949 19.3889 0 12.5 0C5.6104 0 1.41402e-05 5.61109 1.41402e-05 12.5C-0.0102789 19.3896 5.60021 25 12.5 25ZM12.5 2.36312C18.0865 2.36312 22.6367 6.91263 22.6367 12.4998C22.6367 18.0863 18.0872 22.6365 12.5 22.6365C6.91352 22.6365 2.36331 18.087 2.36331 12.4998C2.35987 6.91333 6.90263 2.36312 12.5 2.36312Z" fill="#6B48FF"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.99996 12.5C8.17189 12.5 6.6875 13.9844 6.6875 15.8124C6.6875 17.6407 8.17189 19.1251 9.99996 19.1251C11.828 19.1251 13.3126 17.6407 13.3126 15.8124C13.3126 13.9844 11.828 12.5 9.99996 12.5ZM9.99996 13.5C11.2764 13.5 12.3124 14.5362 12.3124 15.8124C12.3124 17.0888 11.2764 18.1249 9.99996 18.1249C8.72373 18.1249 7.68748 17.0888 7.68748 15.8124C7.68748 14.5362 8.72373 13.5 9.99996 13.5Z" fill="#6B48FF"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.06395 18.4996C6.83368 19.1825 5.99998 20.4942 5.99998 22.0001C5.99998 22.2761 5.7761 22.4999 5.50009 22.4999C5.22387 22.4999 5 22.2761 5 22.0001C5 19.984 6.19579 18.2447 7.91643 17.4539C8.09766 17.3713 8.31088 17.4033 8.45879 17.5361C8.86759 17.9022 9.40821 18.1249 10 18.1249C10.5918 18.1249 11.1324 17.9022 11.5412 17.5361C11.6893 17.4033 11.9023 17.3713 12.0836 17.4539C13.8043 18.2448 15 19.984 15 22.0001C15 22.2761 14.7761 22.4999 14.5001 22.4999C14.2239 22.4999 14 22.2761 14 22.0001C14 20.4942 13.1663 19.1825 11.9362 18.4996C11.3915 18.8933 10.7228 19.1251 10 19.1251C9.27716 19.1251 8.60845 18.8933 8.06395 18.4996Z" fill="#6B48FF"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M11.7505 19.9997V22.4998H12.7507V19.9997H11.7505Z" fill="#6B48FF"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M13.5006 20.75H11.0005V21.75H13.5006V20.75Z" fill="#6B48FF"/>
            <path d="M17.9727 9.21414H21.3546C21.5846 9.21414 21.7707 9.44222 21.7707 9.72113V10.3928C21.7707 10.673 21.5846 10.8997 21.3546 10.8997H17.9727C17.7427 10.8997 17.5566 10.673 17.5566 10.3928V9.72113C17.5566 9.44092 17.7427 9.21414 17.9727 9.21414Z" fill="#6B48FF"/>
            <path d="M15.3436 9.38713H14.6642C14.1342 9.38713 13.7201 8.89362 13.7699 8.32991C13.8197 7.83641 14.25 7.4668 14.714 7.4668H15.5093C15.8076 7.4668 16.056 7.69601 16.122 7.99541C16.1389 8.08351 16.2049 8.13651 16.2877 8.13651H17.1988C17.2984 8.13651 17.3813 8.04841 17.3813 7.9424C17.2816 6.97329 16.553 6.21548 15.625 6.14532V5.1941C15.625 5.08811 15.5422 5 15.4425 5H14.5483C14.4486 5 14.3658 5.0881 14.3658 5.1941V6.16322C13.2392 6.32153 12.3948 7.39664 12.5106 8.66512C12.6265 9.86343 13.6204 10.7265 14.7469 10.7265H15.3267C15.8567 10.7265 16.2708 11.22 16.221 11.7837C16.1712 12.2772 15.7409 12.6468 15.2769 12.6468H14.4816C14.1833 12.6468 13.9348 12.4176 13.8689 12.1182C13.852 12.0301 13.786 11.9771 13.7032 11.9771H12.7921C12.6925 11.9771 12.5935 12.0652 12.6096 12.1712C12.7093 13.1403 13.4379 13.8982 14.3659 13.9683V14.9195C14.3659 15.0255 14.4487 15.1136 14.5484 15.1136H15.4426C15.5423 15.1136 15.6251 15.0255 15.6251 14.9195V13.9325C16.586 13.7563 17.3814 12.9634 17.4803 11.9062C17.6129 10.5503 16.6021 9.38713 15.3436 9.38713Z" fill="#6B48FF"/>
        </svg>`;

        const overpaymentIcon = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="24.0001" height="24.0001" rx="11.5" fill="#2CC09C" stroke="#2CC09C"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.74996 11.25C6.92189 11.25 5.4375 12.7344 5.4375 14.5624C5.4375 16.3907 6.92189 17.8751 8.74996 17.8751C10.578 17.8751 12.0626 16.3907 12.0626 14.5624C12.0626 12.7344 10.578 11.25 8.74996 11.25ZM8.74996 12.25C10.0264 12.25 11.0624 13.2862 11.0624 14.5624C11.0624 15.8388 10.0264 16.8749 8.74996 16.8749C7.47373 16.8749 6.43748 15.8388 6.43748 14.5624C6.43748 13.2862 7.47373 12.25 8.74996 12.25Z" fill="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.81395 17.2496C5.58368 17.9325 4.74998 19.2442 4.74998 20.7501C4.74998 21.0261 4.5261 21.2499 4.25009 21.2499C3.97387 21.2499 3.75 21.0261 3.75 20.7501C3.75 18.734 4.94579 16.9947 6.66643 16.2039C6.84766 16.1213 7.06088 16.1533 7.20879 16.2861C7.61759 16.6522 8.15821 16.8749 8.75 16.8749C9.34179 16.8749 9.88237 16.6522 10.2912 16.2861C10.4393 16.1533 10.6523 16.1213 10.8336 16.2039C12.5543 16.9948 13.75 18.734 13.75 20.7501C13.75 21.0261 13.5261 21.2499 13.2501 21.2499C12.9739 21.2499 12.75 21.0261 12.75 20.7501C12.75 19.2442 11.9163 17.9325 10.6862 17.2496C10.1415 17.6433 9.47284 17.8751 8.75 17.8751C8.02716 17.8751 7.35845 17.6433 6.81395 17.2496Z" fill="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5005 18.7497V21.2498H11.5007V18.7497H10.5005Z" fill="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12.2506 19.5H9.75049V20.5H12.2506V19.5Z" fill="white"/>
            <path d="M21.4458 7.69674H20.2372V6.47282C20.2372 6.34959 20.1389 6.25 20.0173 6.25H19.0836C18.9619 6.25 18.8636 6.34959 18.8636 6.47282V7.69712H17.656C17.5343 7.69712 17.436 7.7967 17.436 7.91993V8.86578C17.436 8.98901 17.5343 9.0886 17.656 9.0886H18.8646V10.3129C18.8646 10.4361 18.9629 10.5357 19.0846 10.5357H20.0183C20.1399 10.5357 20.2382 10.4361 20.2382 10.3129V9.0886H21.4468C21.5685 9.0886 21.6668 8.98901 21.6668 8.86578V7.91993C21.6668 7.7967 21.5685 7.69712 21.4468 7.69712L21.4458 7.69674Z" fill="white"/>
            <path d="M15.3116 8.08783H14.6398C14.1158 8.08783 13.7064 7.59987 13.7556 7.0425C13.8049 6.55454 14.2304 6.18909 14.6891 6.18909H15.4754C15.7704 6.18909 16.0161 6.41571 16.0813 6.71175C16.098 6.79886 16.1632 6.85127 16.2451 6.85127H17.146C17.2445 6.85127 17.3264 6.76416 17.3264 6.65934C17.2279 5.70112 16.5075 4.95182 15.5899 4.88245V3.94192C15.5899 3.83712 15.508 3.75 15.4095 3.75H14.5252C14.4267 3.75 14.3448 3.83711 14.3448 3.94192V4.90015C13.2309 5.05668 12.396 6.11971 12.5105 7.37394C12.625 8.55879 13.6078 9.41217 14.7217 9.41217H15.295C15.819 9.41217 16.2285 9.90013 16.1792 10.4575C16.1299 10.9455 15.7045 11.3109 15.2457 11.3109H14.4594C14.1644 11.3109 13.9187 11.0843 13.8535 10.7882C13.8368 10.7011 13.7716 10.6487 13.6897 10.6487H12.7888C12.6903 10.6487 12.5924 10.7358 12.6084 10.8407C12.7069 11.7989 13.4274 12.5482 14.3449 12.6176V13.5581C14.3449 13.6629 14.4268 13.75 14.5253 13.75H15.4096C15.5081 13.75 15.59 13.6629 15.59 13.5581V12.5822C16.5401 12.4079 17.3265 11.6239 17.4243 10.5786C17.5555 9.23789 16.556 8.08783 15.3116 8.08783Z" fill="white"/>
        </svg>`;

        const fullPaymentIcon = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="24.0001" height="24.0001" rx="11.5" fill="#2CC09C" stroke="#2CC09C"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12.5004 5C9.75832 5 7.53174 7.22657 7.53174 9.96865C7.53174 12.711 9.75832 14.9376 12.5004 14.9376C15.2425 14.9376 17.4694 12.711 17.4694 9.96865C17.4694 7.22657 15.2425 5 12.5004 5ZM12.5004 6.49996C14.4151 6.49996 15.9691 8.05433 15.9691 9.96865C15.9691 11.8833 14.4151 13.4373 12.5004 13.4373C10.5861 13.4373 9.03171 11.8833 9.03171 9.96865C9.03171 8.05433 10.5861 6.49996 12.5004 6.49996Z" fill="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.59593 13.9994C7.75052 15.0237 6.49997 16.9912 6.49997 19.25C6.49997 19.6641 6.16415 19.9999 5.75013 19.9999C5.33581 19.9999 5 19.6641 5 19.25C5 16.2259 6.79368 13.617 9.37464 12.4308C9.64649 12.3069 9.96632 12.3549 10.1882 12.5541C10.8014 13.1033 11.6123 13.4373 12.5 13.4373C13.3877 13.4373 14.1986 13.1033 14.8118 12.5541C15.0339 12.3549 15.3535 12.3069 15.6254 12.4308C18.2064 13.6171 20 16.2259 20 19.25C20 19.6641 19.6642 19.9999 19.2502 19.9999C18.8358 19.9999 18.5 19.6641 18.5 19.25C18.5 16.9912 17.2495 15.0237 15.4044 13.9994C14.5873 14.5899 13.5843 14.9376 12.5 14.9376C11.4157 14.9376 10.4127 14.5898 9.59593 13.9994Z" fill="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M15.126 16.2497V19.9998H16.6262V16.2497H15.126Z" fill="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M17.7511 17.3752H14.001V18.8752H17.7511V17.3752Z" fill="white"/>
        </svg>`;

        const { patientPaymentTag } = encounter;

        if (patientPaymentTag == 5) {
            patientIconTooltip = "Patient paid in full.";
            patientTooltipColor = colorEnum.green;
            return {
                icon: fullPaymentIcon,
                patientIconTooltip,
                patientTooltipColor
            }
        }

        if (
            encounterUnderOrOverPayment.showUnderPayment &&
            encounterUnderOrOverPayment.underPaymentTexts.some((obj) =>
                obj.title.includes("Patient"),
            )
        ) {
            const patientUnderpaid =
                encounterUnderOrOverPayment.underPaymentTexts.find((obj) =>
                    obj.title.includes("Patient"),
                );
            patientIconTooltip = `Patient underpaid ${patientUnderpaid.value}`;
            patientTooltipColor = colorEnum.purple;
            return {
                icon: underpaymentIcon,
                patientIconTooltip,
                patientTooltipColor
            }
        }

        if (
            encounterUnderOrOverPayment.showOverPayment &&
            encounterUnderOrOverPayment.overPaymentTexts.some((obj) =>
                obj.title.includes("Patient"),
            )
        ) {
            const patientOverpaid =
                encounterUnderOrOverPayment.overPaymentTexts.find((obj) =>
                    obj.title.includes("Patient"),
                );
            patientIconTooltip = `Patient overpaid ${patientOverpaid.value}`;
            patientTooltipColor = colorEnum.green;
            return {
                icon: overpaymentIcon,
                patientIconTooltip,
                patientTooltipColor
            }
        }
 
        iconColor = getPaymentTag(patientPaymentTag, "color");
        patientTooltipColor = iconColor;

        patientIconTooltip = getPaymentTag(patientPaymentTag);

        return {
            icon: `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 25C19.4005 25 25 19.3889 25 12.5C25 5.59949 19.3889 0 12.5 0C5.6104 0 1.41402e-05 5.61109 1.41402e-05 12.5C-0.0102789 19.3896 5.60021 25 12.5 25ZM12.5 2.36312C18.0865 2.36312 22.6367 6.91263 22.6367 12.4998C22.6367 18.0863 18.0872 22.6365 12.5 22.6365C6.91352 22.6365 2.36331 18.087 2.36331 12.4998C2.35987 6.91333 6.90263 2.36312 12.5 2.36312Z" fill="${iconColor}"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12.5004 6.25C9.75832 6.25 7.53174 8.47657 7.53174 11.2187C7.53174 13.961 9.75832 16.1876 12.5004 16.1876C15.2425 16.1876 17.4694 13.961 17.4694 11.2187C17.4694 8.47657 15.2425 6.25 12.5004 6.25ZM12.5004 7.74996C14.4151 7.74996 15.9691 9.30433 15.9691 11.2187C15.9691 13.1333 14.4151 14.6873 12.5004 14.6873C10.5861 14.6873 9.03171 13.1333 9.03171 11.2187C9.03171 9.30433 10.5861 7.74996 12.5004 7.74996Z" fill="${iconColor}"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.59593 15.2494C7.75052 16.2737 6.49997 18.2412 6.49997 20.5C6.49997 20.9141 6.16415 21.2499 5.75013 21.2499C5.33581 21.2499 5 20.9141 5 20.5C5 17.4759 6.79368 14.867 9.37464 13.6808C9.64649 13.5569 9.96632 13.6049 10.1882 13.8041C10.8014 14.3533 11.6123 14.6873 12.5 14.6873C13.3877 14.6873 14.1986 14.3533 14.8118 13.8041C15.0339 13.6049 15.3535 13.5569 15.6254 13.6808C18.2064 14.8671 20 17.4759 20 20.5C20 20.9141 19.6642 21.2499 19.2502 21.2499C18.8358 21.2499 18.5 20.9141 18.5 20.5C18.5 18.2412 17.2495 16.2737 15.4044 15.2494C14.5873 15.8399 13.5843 16.1876 12.5 16.1876C11.4157 16.1876 10.4127 15.8398 9.59593 15.2494Z" fill="${iconColor}"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M15.126 17.4997V21.2498H16.6262V17.4997H15.126Z" fill="${iconColor}"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M17.7511 18.6252H14.001V20.1252H17.7511V18.6252Z" fill="${iconColor}"/>
                </svg>`,
            patientIconTooltip,
            patientTooltipColor
        }
    };

    export const getHealthFundIcon = (encounter, encounterUnderOrOverPayment) => {
        let iconColor = colorEnum.gray;
        let healthFundIconTooltip, healthFundTooltipColor;


        const underpaymentIcon = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 25C19.4005 25 25 19.3889 25 12.5C25 5.59949 19.3889 0 12.5 0C5.6104 0 1.41402e-05 5.61109 1.41402e-05 12.5C-0.0102789 19.3896 5.60021 25 12.5 25ZM12.5 2.36312C18.0865 2.36312 22.6367 6.91263 22.6367 12.4998C22.6367 18.0863 18.0872 22.6365 12.5 22.6365C6.91352 22.6365 2.36331 18.087 2.36331 12.4998C2.35987 6.91333 6.90263 2.36312 12.5 2.36312Z" fill="#6B48FF"/>
            <path d="M5.66412 17.0842H7.91424V19.3335C7.91424 19.7002 8.21464 20 8.58094 20H10.1639C10.5307 20 10.8306 19.6997 10.8306 19.3335V17.0842H13.0807C13.4475 17.0842 13.7474 16.7839 13.7474 16.4177L13.75 14.8323C13.75 14.4656 13.4496 14.1658 13.0833 14.1658H10.8332V11.9165C10.8332 11.5498 10.5328 11.25 10.1665 11.25H8.58353C8.21672 11.25 7.91683 11.5503 7.91683 11.9165V14.1658H5.66671C5.2999 14.1658 5.00002 14.4661 5.00002 14.8323V16.4147C4.99742 16.784 5.29731 17.0837 5.66412 17.0837L5.66412 17.0842Z" fill="#6B48FF"/>
            <path d="M17.9727 9.21413H21.3546C21.5846 9.21413 21.7707 9.44221 21.7707 9.72112V10.3928C21.7707 10.673 21.5846 10.8997 21.3546 10.8997H17.9727C17.7427 10.8997 17.5566 10.673 17.5566 10.3928V9.72112C17.5566 9.44091 17.7427 9.21413 17.9727 9.21413Z" fill="#6B48FF"/>
            <path d="M15.3436 9.38713H14.6642C14.1342 9.38713 13.7201 8.89362 13.7699 8.32991C13.8197 7.83641 14.25 7.4668 14.714 7.4668H15.5093C15.8076 7.4668 16.056 7.69601 16.122 7.99541C16.1389 8.08351 16.2049 8.13651 16.2877 8.13651H17.1988C17.2984 8.13651 17.3813 8.04841 17.3813 7.9424C17.2816 6.97329 16.553 6.21548 15.625 6.14532V5.1941C15.625 5.08811 15.5422 5 15.4425 5H14.5483C14.4486 5 14.3658 5.0881 14.3658 5.1941V6.16322C13.2392 6.32153 12.3948 7.39664 12.5106 8.66512C12.6265 9.86343 13.6204 10.7265 14.7469 10.7265H15.3267C15.8567 10.7265 16.2708 11.22 16.221 11.7837C16.1712 12.2772 15.7409 12.6468 15.2769 12.6468H14.4816C14.1833 12.6468 13.9348 12.4176 13.8689 12.1182C13.852 12.0301 13.786 11.9771 13.7032 11.9771H12.7921C12.6925 11.9771 12.5935 12.0652 12.6096 12.1712C12.7093 13.1403 13.4379 13.8982 14.3659 13.9683V14.9195C14.3659 15.0255 14.4487 15.1136 14.5484 15.1136H15.4426C15.5423 15.1136 15.6251 15.0255 15.6251 14.9195V13.9325C16.586 13.7563 17.3814 12.9634 17.4803 11.9062C17.6129 10.5503 16.6021 9.38713 15.3436 9.38713Z" fill="#6B48FF"/>
        </svg>`;

        const overpaymentIcon = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="24.0001" height="24.0001" rx="11.5" fill="#2CC09C" stroke="#2CC09C"/>
            <path d="M5.66412 17.0842H7.91424V19.3335C7.91424 19.7002 8.21464 20 8.58094 20H10.1639C10.5307 20 10.8306 19.6997 10.8306 19.3335V17.0842H13.0807C13.4475 17.0842 13.7474 16.7839 13.7474 16.4177L13.75 14.8323C13.75 14.4656 13.4496 14.1658 13.0833 14.1658H10.8332V11.9165C10.8332 11.5498 10.5328 11.25 10.1665 11.25H8.58353C8.21672 11.25 7.91683 11.5503 7.91683 11.9165V14.1658H5.66671C5.2999 14.1658 5.00002 14.4661 5.00002 14.8323V16.4147C4.99742 16.784 5.29731 17.0837 5.66412 17.0837L5.66412 17.0842Z" fill="white"/>
            <path d="M21.4458 6.44674H20.2372V5.22282C20.2372 5.09959 20.1389 5 20.0173 5H19.0836C18.9619 5 18.8636 5.09959 18.8636 5.22282V6.44712H17.656C17.5343 6.44712 17.436 6.5467 17.436 6.66993V7.61578C17.436 7.73901 17.5343 7.8386 17.656 7.8386H18.8646V9.0629C18.8646 9.18613 18.9629 9.28571 19.0846 9.28571H20.0183C20.1399 9.28571 20.2382 9.18613 20.2382 9.0629V7.8386H21.4468C21.5685 7.8386 21.6668 7.73901 21.6668 7.61578V6.66993C21.6668 6.5467 21.5685 6.44712 21.4468 6.44712L21.4458 6.44674Z" fill="white"/>
            <path d="M15.3116 6.83783H14.6398C14.1158 6.83783 13.7064 6.34987 13.7556 5.7925C13.8049 5.30454 14.2304 4.93909 14.6891 4.93909H15.4754C15.7704 4.93909 16.0161 5.16571 16.0813 5.46175C16.098 5.54886 16.1632 5.60127 16.2451 5.60127H17.146C17.2445 5.60127 17.3264 5.51416 17.3264 5.40934C17.2279 4.45112 16.5075 3.70182 15.5899 3.63245V2.69192C15.5899 2.58712 15.508 2.5 15.4095 2.5H14.5252C14.4267 2.5 14.3448 2.58711 14.3448 2.69192V3.65015C13.2309 3.80668 12.396 4.86971 12.5105 6.12394C12.625 7.30879 13.6078 8.16217 14.7217 8.16217H15.295C15.819 8.16217 16.2285 8.65013 16.1792 9.2075C16.1299 9.69547 15.7045 10.0609 15.2457 10.0609H14.4594C14.1644 10.0609 13.9187 9.83429 13.8535 9.53825C13.8368 9.45114 13.7716 9.39873 13.6897 9.39873H12.7888C12.6903 9.39873 12.5924 9.48584 12.6084 9.59066C12.7069 10.5489 13.4274 11.2982 14.3449 11.3676V12.3081C14.3449 12.4129 14.4268 12.5 14.5253 12.5H15.4096C15.5081 12.5 15.59 12.4129 15.59 12.3081V11.3322C16.5401 11.1579 17.3265 10.3739 17.4243 9.32859C17.5555 7.98789 16.556 6.83783 15.3116 6.83783Z" fill="white"/>
        </svg>`;

        const fullPaymentIcon = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="24.0001" height="24.0001" rx="11.5" fill="#2CC09C" stroke="#2CC09C"/>
            <path d="M7.19874 14.5846H10.4132V17.7979C10.4132 18.3217 10.8423 18.75 11.3656 18.75H13.627C14.151 18.75 14.5794 18.321 14.5794 17.7979V14.5846H17.7939C18.3179 14.5846 18.7463 14.1556 18.7463 13.6325L18.75 11.3675C18.75 10.8437 18.3209 10.4154 17.7976 10.4154H14.5831V7.20209C14.5831 6.67826 14.154 6.25 13.6307 6.25H11.3693C10.8453 6.25 10.4169 6.679 10.4169 7.20209V10.4154H7.20245C6.67843 10.4154 6.25002 10.8444 6.25002 11.3675V13.6281C6.24632 14.1556 6.67473 14.5839 7.19875 14.5839L7.19874 14.5846Z" fill="white"/>
        </svg>`;

        const { insurancePaymentTag } = encounter;

        if (insurancePaymentTag == 5) {
            healthFundIconTooltip = "Health Fund paid is full.";
            healthFundTooltipColor = colorEnum.green;
            return {
                icon: fullPaymentIcon,
                healthFundIconTooltip,
                healthFundTooltipColor
            }
        }

        if (
            encounterUnderOrOverPayment.showUnderPayment &&
            encounterUnderOrOverPayment.underPaymentTexts.some((obj) =>
                obj.title.includes("Health Fund"),
            )
        ) {
            const healthFundUnderpaid =
                encounterUnderOrOverPayment.underPaymentTexts.find((obj) =>
                    obj.title.includes("Health Fund"),
                );
            healthFundIconTooltip = `Health Fund underpaid by ${healthFundUnderpaid.value}`;
            healthFundTooltipColor = colorEnum.purple;
            return {
                icon: underpaymentIcon,
                healthFundIconTooltip,
                healthFundTooltipColor
            }
        }

        if (
            encounterUnderOrOverPayment.showOverPayment &&
            encounterUnderOrOverPayment.overPaymentTexts.some((obj) =>
                obj.title.includes("Health Fund"),
            )
        ) {
            const healthFundOverpaid =
                encounterUnderOrOverPayment.overPaymentTexts.find((obj) =>
                    obj.title.includes("Health Fund"),
                );
            healthFundIconTooltip = `Health Fund overpaid by ${healthFundOverpaid.value}`;
            healthFundTooltipColor = colorEnum.green;
            return {
                icon: overpaymentIcon,
                healthFundIconTooltip,
                healthFundTooltipColor
            }
        }

        iconColor = getPaymentTag(insurancePaymentTag, "color");
        healthFundTooltipColor = iconColor;
        healthFundIconTooltip = getPaymentTag(insurancePaymentTag);

         return {
                icon: `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5 25C19.4005 25 25 19.3889 25 12.5C25 5.59949 19.3889 0 12.5 0C5.6104 0 1.41402e-05 5.61109 1.41402e-05 12.5C-0.0102789 19.3896 5.60021 25 12.5 25ZM12.5 2.36312C18.0865 2.36312 22.6367 6.91263 22.6367 12.4998C22.6367 18.0863 18.0872 22.6365 12.5 22.6365C6.91352 22.6365 2.36331 18.087 2.36331 12.4998C2.35987 6.91333 6.90263 2.36312 12.5 2.36312Z" fill="${iconColor}"/>
                            <path d="M7.19874 14.5846H10.4132V17.7979C10.4132 18.3217 10.8423 18.75 11.3656 18.75H13.627C14.151 18.75 14.5794 18.321 14.5794 17.7979V14.5846H17.7939C18.3179 14.5846 18.7463 14.1556 18.7463 13.6325L18.75 11.3675C18.75 10.8437 18.3209 10.4154 17.7976 10.4154H14.5831V7.20209C14.5831 6.67826 14.154 6.25 13.6307 6.25H11.3693C10.8453 6.25 10.4169 6.679 10.4169 7.20209V10.4154H7.20245C6.67843 10.4154 6.25002 10.8444 6.25002 11.3675V13.6281C6.24632 14.1556 6.67473 14.5839 7.19875 14.5839L7.19874 14.5846Z" fill="${iconColor}"/>
                        </svg>`,
                healthFundIconTooltip,
                healthFundTooltipColor
        }
    };

    export const getSurveyIcon = (encounter) => {
        let surveyIconTooltip, surveyTooltipColor;
        if (!encounter.preOpInstructionsId) return ``;

        const { hasPreOpQuestionnaireSent, hasPreOpQuestionnaireAnswers } = encounter;

        let iconColor = colorEnum.gray;

        const sentIcon = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="25" height="25" rx="12" fill="#2CC09C"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M15.446 13.3588C15.2472 12.7102 14.9366 12.1102 14.5367 11.582C14.4658 11.4879 14.4748 11.358 14.5575 11.2747L15.6513 10.1813L17.016 11.546C17.154 11.6844 17.379 11.6844 17.5177 11.546L17.8491 11.2149C17.9872 11.0765 17.9872 10.8514 17.8491 10.713L16.4845 9.34827L17.8491 7.9841C17.9872 7.84577 17.9872 7.6206 17.8491 7.48228L17.5177 7.15111C17.3796 7.01278 17.1546 7.01278 17.016 7.15111L15.6513 8.51587L14.2867 7.15111C14.1486 7.01278 13.9237 7.01278 13.785 7.15111L13.4535 7.48228C13.3154 7.62061 13.3154 7.84578 13.4535 7.9841L14.8181 9.34886L13.7249 10.443C13.6416 10.5262 13.5118 10.5352 13.4178 10.4639C12.8893 10.0639 12.2894 9.75368 11.6408 9.55486C11.4313 9.49079 11.2968 9.29736 11.3099 9.07818C11.4485 6.80973 13.3345 4.99998 15.6507 4.99998C18.0527 4.99998 19.9999 6.9474 19.9999 9.34937C19.9999 11.6656 18.1895 13.5521 15.9214 13.6909C15.7024 13.7041 15.509 13.57 15.4447 13.3604L15.446 13.3588ZM11.8241 10.6496C9.35434 9.57523 6.48111 10.7058 5.40626 13.176C4.33146 15.6456 5.4628 18.5194 7.93258 19.5938C10.4024 20.6681 13.2756 19.5375 14.3504 17.0678C15.4247 14.5982 14.2939 11.7239 11.8241 10.6496ZM12.4627 14.4156L9.53052 17.0494L9.23057 17.3189C9.13653 17.4033 8.99133 17.3967 8.90563 17.3039L8.63186 17.0087L7.27736 15.5451C7.18095 15.4409 7.18749 15.278 7.29164 15.1816L7.85463 14.6606C7.95818 14.5642 8.12125 14.5708 8.21706 14.675L9.14367 15.6756L11.6069 13.4635C11.7117 13.3688 11.8747 13.3778 11.9693 13.4826L12.4829 14.0533C12.5776 14.1587 12.5692 14.3216 12.4633 14.4162L12.4627 14.4156Z" fill="white"/>
        </svg>`;

        if (hasPreOpQuestionnaireSent && hasPreOpQuestionnaireAnswers) {
            surveyIconTooltip = "Survey Completed";
            surveyTooltipColor = colorEnum.green;
            return {
                icon: sentIcon,
                surveyIconTooltip,
                surveyTooltipColor
            }
        }

        if (hasPreOpQuestionnaireSent) {
            iconColor = colorEnum.purple;
            surveyIconTooltip = "Survey Sent";
        } else surveyIconTooltip = "No Survey Sent";

        surveyTooltipColor = iconColor;

        return {
            icon: `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.5 25C19.4005 25 25 19.3889 25 12.5C25 5.59949 19.3889 0 12.5 0C5.6104 0 1.41402e-05 5.61109 1.41402e-05 12.5C-0.0102789 19.3896 5.60021 25 12.5 25ZM12.5 2.36312C18.0865 2.36312 22.6367 6.91263 22.6367 12.4998C22.6367 18.0863 18.0872 22.6365 12.5 22.6365C6.91352 22.6365 2.36331 18.087 2.36331 12.4998C2.35987 6.91333 6.90263 2.36312 12.5 2.36312Z" fill="${iconColor}"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M15.4461 13.3585C15.2473 12.7099 14.9366 12.1099 14.5367 11.5817C14.4659 11.4876 14.4748 11.3577 14.5575 11.2745L15.6514 10.181L17.016 11.5457C17.1541 11.6841 17.379 11.6841 17.5177 11.5457L17.8492 11.2146C17.9873 11.0762 17.9873 10.8511 17.8492 10.7127L16.4846 9.34796L17.8492 7.98379C17.9873 7.84546 17.9873 7.62029 17.8492 7.48196L17.5177 7.1508C17.3796 7.01246 17.1547 7.01246 17.016 7.1508L15.6514 8.51556L14.2868 7.1508C14.1487 7.01246 13.9237 7.01246 13.7851 7.1508L13.4536 7.48196C13.3155 7.6203 13.3155 7.84547 13.4536 7.98379L14.8182 9.34856L13.7249 10.4427C13.6416 10.5259 13.5119 10.5349 13.4178 10.4636C12.8894 10.0636 12.2895 9.75338 11.6408 9.55456C11.4313 9.49048 11.2968 9.29705 11.3099 9.07787C11.4486 6.80942 13.3345 4.99965 15.6508 4.99965C18.0528 4.99965 20 6.94709 20 9.34907C20 11.6654 18.1896 13.5518 15.9215 13.6907C15.7025 13.7038 15.5091 13.5697 15.4448 13.3601L15.4461 13.3585ZM11.8242 10.6493C9.35436 9.57493 6.48112 10.7056 5.40627 13.1757C4.33146 15.6453 5.4628 18.5192 7.93259 19.5935C10.4024 20.6679 13.2756 19.5372 14.3505 17.0675C15.4247 14.5979 14.294 11.7236 11.8242 10.6493ZM12.4627 14.4154L9.53054 17.0491L9.23059 17.3186C9.13656 17.4031 8.99135 17.3965 8.90565 17.3036L8.63188 17.0084L7.27737 15.5448C7.18096 15.4406 7.1875 15.2777 7.29165 15.1813L7.85464 14.6603C7.9582 14.5639 8.12126 14.5705 8.21708 14.6747L9.14369 15.6754L11.6069 13.4632C11.7117 13.3686 11.8748 13.3775 11.9694 13.4823L12.483 14.053C12.5776 14.1584 12.5693 14.3213 12.4633 14.416L12.4627 14.4154Z" fill="${iconColor}"/>
                    </svg>`,
            surveyIconTooltip,
            surveyTooltipColor
        }
    };
</script>
